import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Skeleton from '@mui/material/Skeleton';
import { CustomDropdownOrangeIcon, DialogCloseIcon } from '../../../../assets/SvgIcons';

interface DropdownOption {
    value?: string | number;
    label?: string;
}

interface CustomDropdownProps {
    isLoading: boolean;
    selectedValue: string | number;
    handleValueChange: (value: string | number) => void;
    dropdownOptions: DropdownOption[];
    searchable?: boolean;
}

export default function CustomDropdown({
    isLoading,
    selectedValue,
    handleValueChange,
    dropdownOptions,
    searchable = false
}: CustomDropdownProps): JSX.Element {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>(dropdownOptions);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const optionRefs = useRef<(HTMLElement | null)[]>([]);
    const listboxRef = useRef<HTMLDivElement | null>(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        setFilteredOptions(dropdownOptions);
    }, [dropdownOptions]);

    // A5: Focus the first option when the popover opens so keyboard navigation works immediately.
    useEffect(() => {
        if (open) {
            setTimeout(() => {
                if (optionRefs.current[0]) {
                    optionRefs.current[0].focus();
                    setFocusedIndex(0);
                } else {
                    listboxRef.current?.focus();
                }
            }, 0);
        }
    }, [open]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setSearchText('');
        setFocusedIndex(-1);
        // Move focus to first option once the popover renders
        setTimeout(() => {
            if (optionRefs.current[0]) {
                optionRefs.current[0].focus();
                setFocusedIndex(0);
            } else {
                listboxRef.current?.focus();
            }
        }, 0);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchText('');
        setFilteredOptions(dropdownOptions);
        setFocusedIndex(-1);
    };

    const handleClearSearch = () => {
        setSearchText('');
        setFilteredOptions(dropdownOptions);
    };

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const query = event.target.value.toLowerCase();
        setSearchText(event.target.value);
        setFocusedIndex(-1);

        const filtered = dropdownOptions.filter(option =>
            option?.label?.toLowerCase().includes(query)
        );
        setFilteredOptions(filtered);
    };

    const handleSelect = (optionValue: string | number) => {
        handleValueChange(optionValue);
        handleClose();
    };

    if (isLoading) {
        return (
            <Skeleton animation="wave" width={200} height={30} />
        );
    }

    return (
        <>
            {/* Main Dropdown Button - EXACT same styling */}
            <Box
                onClick={handleClick}
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={`Select option, current: ${dropdownOptions?.find(opt => opt.value === selectedValue)?.label || ''}`}
                tabIndex={0}
                onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        handleClick(e as unknown as React.MouseEvent<HTMLElement>);
                    }
                }}
                sx={{
                    ...theme.typography.body_500,
                    color: theme.palette.customColors.subtitle,
                    cursor: 'pointer',
                    padding: { xs: "4px 2px", sm: "7px 5px" },
                    borderBottom: `1px dashed ${theme.palette.text.secondary}B2`,
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'transparent',
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                    },
                }}
            >
                <Typography variant="body_500" sx={{ flex: 1 }}>
                    {dropdownOptions?.find(opt => opt.value === selectedValue)?.label || ''}
                </Typography>
                <CustomDropdownOrangeIcon />
            </Box>

            {/* Popover - EXACT same styling */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            padding: "2px",
                            borderRadius: "12px",
                            "& .MuiListItemButton-root": {
                                padding: "6px",
                                borderRadius: "8px",
                                ...theme.typography.body_500,
                                color: theme.palette.customColors.subtitle,
                                my: 0.4,
                                "&.Mui-selected": {
                                    color: theme.palette.customColors.subtitle,
                                },
                                [theme.breakpoints.down("sm")]: {
                                    padding: "4px 6px",
                                    fontSize: "0.8rem",
                                    minHeight: "32px",
                                },
                            },
                            "& .MuiListItemButton-root:hover": {
                                color: theme.palette.customColors.subtitle,
                            },
                        },
                    }
                }}
            >
                {/* Search Input - EXACT same */}
                {searchable && (
                    <Box sx={{ p: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearchTextChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText?.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onMouseDown={handleClearSearch}>
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '40px',
                                    borderRadius: '25px',
                                },
                                '& input': {
                                    paddingLeft: '10px',
                                    ...theme.typography.body_500,
                                }
                            }}
                        />
                    </Box>
                )}

                {/* Options List - EXACT same styling */}
                <Box
                    ref={listboxRef}
                    role="listbox"
                    aria-label="Options"
                    tabIndex={0}
                    sx={{ maxHeight: 250, overflow: 'auto' }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const nextIndex = Math.min(focusedIndex + 1, filteredOptions.length - 1);
                            setFocusedIndex(nextIndex);
                            optionRefs.current[nextIndex]?.focus();
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            const prevIndex = Math.max(focusedIndex - 1, 0);
                            setFocusedIndex(prevIndex);
                            optionRefs.current[prevIndex]?.focus();
                        } else if ((e.key === 'Enter' || e.key === ' ') && focusedIndex >= 0) {
                            e.preventDefault();
                            handleSelect(filteredOptions[focusedIndex]?.value ?? '');
                        } else if (e.key === 'Escape') {
                            handleClose();
                        }
                    }}
                >
                    {filteredOptions?.length > 0 ? (
                        filteredOptions?.map((option, index) => (
                            <ListItemButton
                                key={option.value}
                                role="option"
                                aria-selected={selectedValue === option.value}
                                tabIndex={focusedIndex === index ? 0 : -1}
                                ref={(el) => { optionRefs.current[index] = el; }}
                                selected={selectedValue === option.value}
                                onClick={() => handleSelect(option?.value ?? '')}
                                sx={{
                                    padding: '7px 5px',
                                    borderRadius: '8px',
                                    margin: '4px',
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary[200],
                                    },
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    }
                                }}
                            >
                                <Typography variant="body_500">{option.label}</Typography>
                            </ListItemButton>
                        ))
                    ) : (
                        <Typography variant="body_500" sx={{ p: 1, textAlign: 'center', color: 'text.secondary' }}>
                            No options found
                        </Typography>
                    )}
                </Box>
            </Popover>
        </>
    );
}